import { Component, memo, useMemo, type ReactNode } from "react";
import { Link } from "react-router";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ElementContent, Nodes, Root } from "hast";
import { baseComponents, MARKDOWN_LINK_CLASS, textOf } from "./markdown-base";

// Markdown written by a PERSON — the description of a shared item — as opposed to the
// assistant's replies, which AgentMarkdown renders with its person and company grammar.
//
// No image is ever loaded: a description is reachable by everyone the item is shared with, so a
// `![](url)` would be a tracking pixel from an arbitrary host inside the reader's page. It renders
// as a link to the image instead (spark#1096).
//
// Only `/path` counts as same-origin: `//host/path` is protocol-relative, the sanitiser lets it
// through (no scheme), and react-router would render it as a bare anchor to another origin — so
// it leaves the page like any other external link. A `#fragment` has nothing to point at here,
// since headings carry no ids (below).
//
// A link whose text itself reads as a URL of a different host — `[https://linkedin.com/…](https://
// evil.example)` — shows the real destination instead, so the visible URL is never a lie. Any
// other link text is the author's to choose.
//
// Headings carry no `id`. The slug the shared mapping derives from the heading text would land in
// the page unprefixed, so `## share-stat-views` would duplicate an id the page already uses; a
// person's description gets no table of contents anyway.
//
// Footnotes (`[^1]`) are not rendered: remark-gfm emits their list under a hard-coded English
// heading, and the reference links would need ids this renderer drops. The reference itself
// remains a superscript number without a target.
//
// Single line breaks are KEPT. Markdown folds a lone newline into a space, but every description
// written before this renderer existed used newlines as line breaks and was shown that way
// (`whitespace-pre-wrap`); the paragraph keeps that rule, so such a description looks as it did.
// The rule sits on the paragraph and the list item, not on the container, because react-markdown
// emits a newline text node between blocks and pre-wrap on the container would show each as an
// empty line.
//
// Three guards keep a pathological description from taking the page down for every reader, each
// ending in the plain text the description always was: one longer than MAX_MARKDOWN_CHARS is not
// parsed at all (parsing is quadratic in places — 40 000 chars of `*_` took 4.5 s); a tree nested
// deeper than MAX_MARKDOWN_DEPTH is refused before React sees it (2 000 chars of `> - ` parse
// fine, then overflow the stack in React's commit phase, where no error boundary reaches and the
// whole SPA stops rendering); and a parse that throws (2 500 `>` in a row) is caught. The depth
// check is a rehype step because react-markdown runs those inside its render, within ParseGuard.

// A pasted LinkedIn URL is one unbroken word; without break-words it widens the card.
const LINK_CLASS = `${MARKDOWN_LINK_CLASS} break-words`;

export const MAX_MARKDOWN_CHARS = 10_000;
export const MAX_MARKDOWN_DEPTH = 64;

function rehypeLimitDepth() {
  return (tree: Root) => {
    const stack: Array<[Nodes, number]> = [[tree, 0]];
    for (let next = stack.pop(); next; next = stack.pop()) {
      const [node, depth] = next;
      if (depth > MAX_MARKDOWN_DEPTH) throw new Error("markdown nested too deeply");
      if ("children" in node) for (const child of node.children) stack.push([child, depth + 1]);
    }
  };
}

const SAME_ORIGIN_PATH = /^\/(?![/\\])/;

// The `↗` rides a text node, not the `inline-flex gap-1` of a button: an inline-flex anchor would
// stop wrapping inside prose, and a long URL has to wrap.
function LeavingLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
      {children}
      <span aria-hidden="true"> ↗</span>
    </a>
  );
}

function hostOf(url: string): string | undefined {
  try {
    return new URL(url.startsWith("www.") ? `http://${url}` : url).host.toLowerCase();
  } catch {
    return undefined;
  }
}

// A URL-looking token anywhere in the text: a scheme, `www.`, or a dotted host followed by a path.
// The path is required for the bare form so that "z.B." or "e.g." never count as a host.
const URL_IN_TEXT = /(?:^|\s)((?:https?:\/\/|www\.|[\w-]+(?:\.[\w-]+)+\/)\S*)/i;

/** The link text to show: the real destination when the text claims to be a URL of another host. */
function honestLinkText(href: string, children: ReactNode): ReactNode {
  // Format characters (zero-width space and friends) are invisible, so they are not part of what
  // the reader sees and must not hide the URL from the check.
  const text = textOf(children).replace(/\p{Cf}/gu, "");
  const claimed = URL_IN_TEXT.exec(text)?.[1];
  if (!claimed) return children;
  const claimedHost = hostOf(claimed);
  if (claimedHost && claimedHost === hostOf(href)) return children;
  return href;
}

// `[![alt](img)](href)`: the image would render as a link of its own inside the link. The label
// is the alt text instead.
function hastText(nodes: ElementContent[]): string {
  return nodes
    .map((n) => {
      if (n.type === "text") return n.value;
      if (n.type !== "element") return "";
      if (n.tagName === "img") return String(n.properties.alt || n.properties.src || "");
      return hastText(n.children);
    })
    .join("");
}

function containsImage(nodes: ElementContent[]): boolean {
  return nodes.some(
    (n) => n.type === "element" && (n.tagName === "img" || containsImage(n.children)),
  );
}

const Heading = ({ children }: { children?: ReactNode }) => (
  <h3 className="mb-1 mt-2 font-semibold first:mt-0">{children}</h3>
);

const components: Components = {
  ...baseComponents,
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
  p: ({ children }) => <p className="mb-2 whitespace-pre-wrap last:mb-0">{children}</p>,
  li: ({ children }) => <li className="whitespace-pre-wrap pl-0.5">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-ink/15 pl-2 text-ink last:mb-0">{children}</blockquote>
  ),
  a: ({ href, children, node }) => {
    // The sanitiser answers an unsafe scheme with an empty href: text, then, not a dead anchor.
    if (!href || href.startsWith("#")) return <>{children}</>;
    const label = honestLinkText(
      href,
      node && containsImage(node.children) ? hastText(node.children) : children,
    );
    if (SAME_ORIGIN_PATH.test(href)) {
      return (
        <Link to={href} className={LINK_CLASS}>
          {label}
        </Link>
      );
    }
    return <LeavingLink href={href}>{label}</LeavingLink>;
  },
  img: ({ src, alt }) => {
    const url = typeof src === "string" ? src : "";
    if (!url) return alt ? <span className="text-ink-muted">{alt}</span> : null;
    return <LeavingLink href={url}>{alt || url}</LeavingLink>;
  },
};

const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeLimitDepth];
const disallowedElements = ["section"];

function PlainText({ content }: { content: string }) {
  return <div className="whitespace-pre-wrap">{content}</div>;
}

class ParseGuard extends Component<{ content: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(prev: { content: string }) {
    if (prev.content !== this.props.content && this.state.failed) this.setState({ failed: false });
  }

  render() {
    return this.state.failed ? <PlainText content={this.props.content} /> : this.props.children;
  }
}

export const MarkdownText = memo(function MarkdownText({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  // react-markdown re-parses on every render; the description is static while it is read.
  const rendered = useMemo(() => {
    if (content.length > MAX_MARKDOWN_CHARS) return <PlainText content={content} />;
    return (
      <ParseGuard content={content}>
        <Markdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={components}
          disallowedElements={disallowedElements}
        >
          {content}
        </Markdown>
      </ParseGuard>
    );
  }, [content]);
  return <div className={className}>{rendered}</div>;
});
